/*
 * Free gift offer runtime (free Everyday Mini over a spend threshold).
 *
 * - <court-gift-countdown ends-at="ISO"> renders a live countdown and, on expiry,
 *   hides the closest [data-court-gift-offer] container.
 * - Keeps exactly one gift line (marked `_gift_offer: court-bag`) in the cart
 *   while the cart subtotal, excluding gift lines, reaches the threshold, and
 *   removes it otherwise. The $0 price itself comes from an automatic discount
 *   at checkout.
 * - Updates every [data-court-gift-progress] bar and broadcasts `court-gift:update`
 *   ({ qualifies, added, subtotal, threshold }) so the page can follow along
 *   without a reload.
 */
(function () {
  'use strict';

  const configElement = document.getElementById('court-gift-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('[Court gift] Invalid config', error);
    return;
  }

  const OFFER = 'court-bag';
  const VARIANT_KEY = 'cancha_court_gift_variant';
  const ROOT = window.Shopify?.routes?.root || '/';
  const endsAt = config.endsAt ? Date.parse(config.endsAt) : NaN;

  function isActive() {
    return Number.isNaN(endsAt) || Date.now() < endsAt;
  }

  // ── Countdown ─────────────────────────────────────────────

  const pad = (value) => String(value).padStart(2, '0');

  if (!customElements.get('court-gift-countdown')) {
    class CourtGiftCountdown extends HTMLElement {
      connectedCallback() {
        this.endsAt = Date.parse(this.getAttribute('ends-at'));
        if (Number.isNaN(this.endsAt)) return;
        this.units = {
          days: this.querySelector('[data-unit="days"]'),
          hours: this.querySelector('[data-unit="hours"]'),
          minutes: this.querySelector('[data-unit="minutes"]'),
          seconds: this.querySelector('[data-unit="seconds"]'),
        };
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      tick() {
        const remaining = Math.max(0, this.endsAt - Date.now());
        const total = Math.floor(remaining / 1000);
        const values = {
          days: Math.floor(total / 86400),
          hours: Math.floor((total % 86400) / 3600),
          minutes: Math.floor((total % 3600) / 60),
          seconds: total % 60,
        };
        Object.entries(values).forEach(([unit, value]) => {
          const element = this.units[unit];
          if (element && element.textContent !== pad(value)) element.textContent = pad(value);
        });
        this.removeAttribute('hidden');

        if (remaining === 0) {
          clearInterval(this.timer);
          const container = this.closest('[data-court-gift-offer]');
          if (container) container.hidden = true;
        }
      }
    }

    customElements.define('court-gift-countdown', CourtGiftCountdown);
  }

  // ── Cart sync ─────────────────────────────────────────────

  const thresholdCents = Number(config.thresholdCents) || 0;
  const giftVariantIds = config.giftVariantIds || [];
  let syncing = false;
  let refreshingSelf = false;
  let syncPending = false;
  let pendingVariantId = null;

  function isGiftLine(item) {
    return item.properties && item.properties._gift_offer === OFFER;
  }

  // Matches snippets/cart-gift-subtotal.liquid: every line except marked gifts.
  function qualifyingSubtotal(cart) {
    return cart.items.reduce((total, item) => {
      if (isGiftLine(item) || (item.properties && item.properties._gift === 'true')) return total;
      return total + (item.final_line_price || 0);
    }, 0);
  }

  function formatMoney(cents, currency) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      }).format(amount);
    } catch (error) {
      return amount.toFixed(Number.isInteger(amount) ? 0 : 2);
    }
  }

  function preferredVariantId() {
    let stored = null;
    try {
      stored = parseInt(localStorage.getItem(VARIANT_KEY), 10);
    } catch (error) {
      // Storage can be unavailable (private mode); fall back to the default.
    }
    return giftVariantIds.includes(stored) ? stored : giftVariantIds[0];
  }

  async function post(path, body) {
    const response = await fetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Court gift request failed: ${path}`);
    return response.json();
  }

  function addGift(variantId) {
    return post('cart/add.js', {
      items: [{ id: variantId, quantity: 1, properties: { _gift: 'true', _gift_offer: OFFER } }],
    });
  }

  function setQuantity(key, quantity) {
    return post('cart/change.js', { id: key, quantity });
  }

  function broadcast(qualifies, subtotal, currency) {
    const state = qualifies ? 'added' : 'locked';
    const percent = thresholdCents > 0 ? Math.min(100, Math.floor((subtotal * 100) / thresholdCents)) : 100;
    const remaining = formatMoney(Math.max(0, thresholdCents - subtotal), currency);
    document.querySelectorAll('[data-court-gift-progress]').forEach((element) => {
      element.dataset.state = state;
      element.style.setProperty('--court-gift-progress', `${percent}%`);
      const track = element.querySelector('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', String(qualifies ? 100 : percent));
      const label = element.querySelector('[data-court-gift-progress-label]');
      const text = qualifies ? element.dataset.labelAdded : element.dataset.labelLocked;
      if (label && text) label.textContent = text.replace('[amount]', remaining);
    });
    document.dispatchEvent(new CustomEvent('court-gift:update', {
      detail: { qualifies, added: qualifies, subtotal, threshold: thresholdCents },
    }));
  }

  async function sync(cart, chosenVariantId) {
    if (syncing) {
      // Re-read the cart afterwards: a cart passed with a queued event may predate our own changes.
      syncPending = true;
      pendingVariantId = chosenVariantId || pendingVariantId;
      return;
    }
    syncing = true;

    try {
      if (!cart) cart = await (await fetch(ROOT + 'cart.js', { cache: 'no-store' })).json();

      const giftLines = cart.items.filter(isGiftLine);
      const subtotal = qualifyingSubtotal(cart);
      const qualifies = isActive() && giftVariantIds.length > 0 && subtotal >= thresholdCents;
      let keep = qualifies ? giftLines[0] : null;
      let changed = false;

      // A colour change swaps the gift line rather than editing it in place.
      if (keep && chosenVariantId && keep.variant_id !== chosenVariantId) keep = null;

      // One gift per order: drop duplicates, or every gift line once the cart no longer qualifies.
      for (const line of giftLines) {
        if (line === keep) continue;
        await setQuantity(line.key, 0);
        changed = true;
      }

      if (qualifies && !keep) {
        await addGift(chosenVariantId || preferredVariantId());
        changed = true;
      } else if (qualifies && keep.quantity > 1) {
        await setQuantity(keep.key, 1);
        changed = true;
      }

      broadcast(qualifies, subtotal, cart.currency);

      if (changed) {
        refreshingSelf = true;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        refreshingSelf = false;
      }
    } catch (error) {
      console.error('[Court gift]', error);
    } finally {
      syncing = false;
      document.querySelectorAll('[data-court-gift-colours][aria-busy]').forEach((picker) => picker.removeAttribute('aria-busy'));
      if (syncPending) {
        syncPending = false;
        const variantId = pendingVariantId;
        pendingVariantId = null;
        sync(null, variantId);
      }
    }
  }

  document.addEventListener('cart:change', (event) => sync(event.detail?.cart || null));
  // Some flows (bundle builder, other gift scripts) only fire cart:refresh.
  document.addEventListener('cart:refresh', () => {
    if (!refreshingSelf) sync(null);
  });

  // Colour swatches in the cart card. Before the gift is unlocked a pick is only remembered
  // (and used when the gift is added); once it is in the cart, a pick swaps the gift line.
  function markColour(variantId) {
    document.querySelectorAll('[data-court-gift-colours]').forEach((picker) => {
      let name = '';
      picker.querySelectorAll('[data-court-gift-variant-option]').forEach((swatch) => {
        const checked = parseInt(swatch.dataset.courtGiftVariantOption, 10) === variantId;
        swatch.setAttribute('aria-checked', String(checked));
        if (checked) name = swatch.dataset.colourName || '';
      });
      const label = picker.querySelector('[data-court-gift-colour-name]');
      if (label) label.textContent = name;
    });
  }

  function showStoredColour() {
    // Server-rendered state wins once the gift is in the cart (a swatch is already checked).
    if (document.querySelector('[data-court-gift-variant-option][aria-checked="true"]')) return;
    markColour(preferredVariantId());
  }

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.('[data-court-gift-variant-option]');
    if (!swatch || swatch.disabled) return;
    const variantId = parseInt(swatch.dataset.courtGiftVariantOption, 10);
    if (!giftVariantIds.includes(variantId)) return;
    try {
      localStorage.setItem(VARIANT_KEY, String(variantId));
    } catch (error) {
      // Ignore storage failures; the swap below still happens.
    }
    markColour(variantId);
    const picker = swatch.closest('[data-court-gift-colours]');
    if (picker) picker.setAttribute('aria-busy', 'true');
    sync(null, variantId);
  });

  // The cart drawer and cart page re-render their markup after cart changes, so restore the
  // remembered colour whenever a fresh picker appears.
  new MutationObserver(() => {
    const fresh = document.querySelector('[data-court-gift-colours]:not([data-ready])');
    if (!fresh) return;
    document.querySelectorAll('[data-court-gift-colours]').forEach((picker) => picker.setAttribute('data-ready', ''));
    showStoredColour();
  }).observe(document.body, { childList: true, subtree: true });
  document.querySelectorAll('[data-court-gift-colours]').forEach((picker) => picker.setAttribute('data-ready', ''));
  showStoredColour();

  sync(null);

  // Expose for tests and debugging.
  window.CanchaCourtGift = { sync, isActive };
})();
